import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { useFamily } from '../../contexts/FamilyContext';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { signOut } from 'firebase/auth';
import { auth } from '../../lib/firebase';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faHouse, 
  faList, 
  faComments, 
  faBook, 
  faGear, 
  faCircleQuestion,
  faRightFromBracket,
  faChevronLeft,
  faChevronRight,
  faChevronDown,
  faChevronUp,
  faSearch,
  faGripVertical,
  faShield
} from '@fortawesome/free-solid-svg-icons';
import toast from 'react-hot-toast';
import { sortExperiencesByDate, filterExperiencesByStatus } from '../../utils/experiences';
import { getExperienceType } from '../../data/experienceTypes';
import { useIsAdmin } from '../../utils/admin';

const SIDEBAR_STORAGE_KEY = 'sidebarCollapsed';
const SIDEBAR_WIDTH_KEY = 'sidebarWidth';

const navItems = [
  { id: 'dashboard', icon: faHouse, label: 'Dashboard', path: '/' },
  { id: 'experiences', icon: faList, label: 'Experiences', path: '/experiences' },
  { id: 'explore', icon: faBook, label: 'Explore', path: '/explore' },
  { id: 'chats', icon: faComments, label: 'Chats', path: '/chats' },
  { id: 'settings', icon: faGear, label: 'Settings', path: '/settings' },
];

export default function Sidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [sidebarWidth, setSidebarWidth] = useState(240);
  const [activeExperiencesCount, setActiveExperiencesCount] = useState(0);
  const [unreadChatsCount, setUnreadChatsCount] = useState(0);
  const [profileImageError, setProfileImageError] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showExperiencesMenu, setShowExperiencesMenu] = useState(false);
  const [showSearchPalette, setShowSearchPalette] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [recentExperiences, setRecentExperiences] = useState([]);
  const [isResizing, setIsResizing] = useState(false);
  const userMenuRef = useRef(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0, width: 0 });
  const { user } = useAuth();
  const { family, familyId } = useFamily();
  const { isAdmin } = useIsAdmin(user?.uid);

  // Reset profile image error when user or photoURL changes
  useEffect(() => {
    setProfileImageError(false);
  }, [user?.photoURL]);

  // Load collapsed state and width from localStorage and set initial margin
  useEffect(() => {
    const savedCollapsed = localStorage.getItem(SIDEBAR_STORAGE_KEY);
    let initialCollapsed = false;
    if (savedCollapsed !== null) {
      initialCollapsed = JSON.parse(savedCollapsed);
      setCollapsed(initialCollapsed);
    }
    
    const savedWidth = localStorage.getItem(SIDEBAR_WIDTH_KEY);
    let initialWidth = 240;
    if (savedWidth !== null) {
      const width = parseInt(savedWidth, 10);
      if (width >= 200 && width <= 400) {
        initialWidth = width;
        setSidebarWidth(width);
      }
    }
    
    // Set initial margin and width immediately based on loaded values
    // Only apply margin on routes that should show the sidebar (not login/onboarding)
    const mainContent = document.getElementById('main-content');
    const currentPath = window.location.pathname;
    const shouldShowSidebar = window.innerWidth >= 768 && 
      !currentPath.startsWith('/login') && 
      !currentPath.startsWith('/onboarding') &&
      !currentPath.startsWith('/accept-invite');
    
    if (mainContent && shouldShowSidebar) {
      const sidebarWidthPx = initialCollapsed ? 64 : initialWidth;
      mainContent.style.marginLeft = `${sidebarWidthPx}px`;
      mainContent.style.width = `calc(100vw - ${sidebarWidthPx}px)`;
    } else if (mainContent) {
      // Reset margin for login/onboarding pages
      mainContent.style.marginLeft = '0';
      mainContent.style.width = '100vw';
    }
  }, []);

  // Save collapsed state and width to localStorage
  useEffect(() => {
    localStorage.setItem(SIDEBAR_STORAGE_KEY, JSON.stringify(collapsed));
  }, [collapsed]);

  useEffect(() => {
    localStorage.setItem(SIDEBAR_WIDTH_KEY, sidebarWidth.toString());
  }, [sidebarWidth]);

  // Load experiences (for count and quick switcher)
  useEffect(() => {
    if (!familyId) return;

    const q = query(
      collection(db, 'projects'),
      where('familyId', '==', familyId)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const experiences = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      // Count active experiences (planning or upcoming status)
      const active = experiences.filter(e => {
        const status = e.status || 'planning';
        return status === 'planning' || status === 'upcoming';
      });
      setActiveExperiencesCount(active.length);
      
      // Get recent experiences (sorted by updatedAt, max 5)
      const recent = sortExperiencesByDate(
        filterExperiencesByStatus(experiences, 'active')
      ).slice(0, 5);
      setRecentExperiences(recent);
    }, (error) => {
      console.error('Error loading experiences:', error);
    });

    return unsubscribe;
  }, [familyId]);

  // Load unread chats count (placeholder - implement when unread tracking is added)
  useEffect(() => {
    // TODO: Implement unread chats tracking
    setUnreadChatsCount(0);
  }, [familyId]);

  const toggleSidebar = () => {
    setCollapsed(!collapsed);
  };

  // Update main content margin and width when collapsed state or width changes
  useEffect(() => {
    const updateMainContentLayout = () => {
      const mainContent = document.getElementById('main-content');
      if (!mainContent) return;
      
      const currentPath = location.pathname;
      const shouldShowSidebar = window.innerWidth >= 768 && 
        !currentPath.startsWith('/login') && 
        !currentPath.startsWith('/onboarding') &&
        !currentPath.startsWith('/accept-invite');
      
      if (shouldShowSidebar) {
        const sidebarWidthPx = collapsed ? 64 : sidebarWidth;
        mainContent.style.marginLeft = `${sidebarWidthPx}px`;
        mainContent.style.width = `calc(100vw - ${sidebarWidthPx}px)`;
      } else {
        // Reset margin for login/onboarding pages
        mainContent.style.marginLeft = '0';
        mainContent.style.width = '100vw';
      }
    };

    // Set immediately
    updateMainContentLayout();
    
    // Also set on next frame to ensure it's applied after any DOM updates
    requestAnimationFrame(updateMainContentLayout);

    // Handle window resize
    const handleResize = () => {
      updateMainContentLayout();
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [collapsed, sidebarWidth]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
      const modKey = isMac ? e.metaKey : e.ctrlKey;
      
      // Cmd/Ctrl + B: Toggle sidebar
      if (modKey && e.key === 'b') {
        e.preventDefault();
        toggleSidebar();
        return;
      }
      
      // Cmd/Ctrl + K: Open search palette
      if (modKey && e.key === 'k') {
        e.preventDefault();
        setShowSearchPalette(true);
        return;
      }
      
      // Cmd/Ctrl + 1-5: Navigate to sections
      if (modKey && e.key >= '1' && e.key <= '5') {
        e.preventDefault();
        const index = parseInt(e.key) - 1;
        if (navItems[index]) {
          handleNavClick(navItems[index]);
        }
        return;
      }
      
      // Escape: Close modals/palettes
      if (e.key === 'Escape') {
        setShowSearchPalette(false);
        setShowExperiencesMenu(false);
        setShowUserMenu(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Handle resize drag
  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (e) => {
      const newWidth = e.clientX;
      if (newWidth >= 200 && newWidth <= 400) {
        setSidebarWidth(newWidth);
      }
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing]);

  // Close menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showUserMenu && !e.target.closest('.user-menu-container')) {
        setShowUserMenu(false);
      }
      if (showExperiencesMenu && !e.target.closest('.experiences-menu-container')) {
        setShowExperiencesMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showUserMenu, showExperiencesMenu]);

  const handleNavClick = (item) => {
    // Always navigate to the page
    navigate(item.path);
    setShowExperiencesMenu(false);
  };

  const handleExperienceClick = (experienceId) => {
    navigate(`/experience/${experienceId}`);
    setShowExperiencesMenu(false);
  };

  const getKeyboardShortcut = (index) => {
    const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
    return isMac ? `⌘${index + 1}` : `Ctrl+${index + 1}`;
  };

  const handleSignOut = async () => {
    try {
      await signOut(auth);
      toast.success('Signed out successfully');
      navigate('/login');
    } catch (error) {
      console.error('Error signing out:', error);
      toast.error('Failed to sign out');
    }
  };

  const getActiveRoute = () => {
    const path = location.pathname;
    if (path === '/') return 'dashboard';
    if (path === '/experiences' || path.startsWith('/experience') || path.startsWith('/project')) return 'experiences';
    if (path.startsWith('/chats')) return 'chats';
    if (path.startsWith('/explore')) return 'explore';
    if (path.startsWith('/settings')) return 'settings';
    if (path.startsWith('/admin')) return 'admin';
    return 'dashboard';
  };

  const activeRoute = getActiveRoute();

  const getBadgeCount = (itemId) => {
    if (itemId === 'experiences') return activeExperiencesCount > 0 ? activeExperiencesCount : null;
    if (itemId === 'chats') return unreadChatsCount > 0 ? unreadChatsCount : null;
    return null;
  };

  // Don't show sidebar on login, onboarding, or public routes
  const hideSidebarPaths = ['/login', '/onboarding', '/accept-invite'];
  const shouldShowSidebar = !hideSidebarPaths.some(path => location.pathname.startsWith(path)) && user;

  if (!shouldShowSidebar) {
    return null;
  }

  return (
    <>
      {/* Desktop Sidebar - hidden on mobile */}
      <aside 
        className="hidden md:flex fixed left-0 top-0 h-screen bg-white border-r border-border z-10 flex-col transition-all duration-300 overflow-x-hidden"
        style={{ 
          width: collapsed ? '64px' : `${sidebarWidth}px`,
        }}
      >
        {/* Resize Handle */}
        {!collapsed && (
          <div
            onMouseDown={() => setIsResizing(true)}
            className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-primary/20 transition-colors group"
            style={{ zIndex: 10 }}
          >
            <div className="absolute right-0 top-1/2 -translate-y-1/2 w-1 h-12 bg-border group-hover:bg-primary rounded-full" />
          </div>
        )}

        {/* Search Button (when collapsed) or Search Bar */}
        {collapsed ? (
          <button
            onClick={() => setShowSearchPalette(true)}
            className="m-2 p-0 rounded-lg text-text-secondary hover:bg-surface hover:text-text-primary transition-colors flex items-center justify-center w-auto"
            title="Search (⌘K)"
            style={{ 
              minHeight: '44px',
              minWidth: '44px',
              width: '44px',
              height: '44px'
            }}
          >
            <FontAwesomeIcon icon={faSearch} className="w-5 h-5 mx-auto" />
          </button>
        ) : (
          <div className="p-4 border-b border-border overflow-x-hidden">
            <button
              onClick={() => setShowSearchPalette(true)}
              className="w-full flex items-center gap-3 px-3 py-2 bg-surface rounded-lg text-text-secondary hover:bg-[#E0E0E0] transition-colors text-sm max-w-full"
            >
              <FontAwesomeIcon icon={faSearch} className="w-4 h-4" />
              <span                   className="flex-1 text-left truncate">Search...</span>
                  <span className="text-xs opacity-60 flex-shrink-0">⌘K</span>
            </button>
          </div>
        )}
        {/* User Section / Family Units Switcher */}
        <div 
          ref={userMenuRef}
          className={`border-b border-border relative user-menu-container overflow-x-hidden ${collapsed ? 'p-2' : 'p-4'}`}
        >
          <div className={`flex items-center ${collapsed ? 'justify-center' : 'gap-3'}`}>
            {user?.photoURL && !profileImageError ? (
              <img 
                src={user.photoURL} 
                alt={user.displayName || 'Profile'} 
                className="w-10 h-10 rounded-full flex-shrink-0 object-cover"
                style={{ minWidth: '40px', minHeight: '40px' }}
                onError={() => {
                  console.error('Profile picture failed to load:', user.photoURL);
                  setProfileImageError(true);
                }}
              />
            ) : (
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0" style={{ minWidth: '40px', minHeight: '40px' }}>
                <span className="text-primary text-lg font-semibold">
                  {user?.displayName?.[0]?.toUpperCase() || user?.email?.[0]?.toUpperCase() || '👤'}
                </span>
              </div>
            )}
            <AnimatePresence>
              {!collapsed && (
                <motion.div
                  initial={{ opacity: 0, width: 0 }}
                  animate={{ opacity: 1, width: 'auto' }}
                  exit={{ opacity: 0, width: 0 }}
                  transition={{ duration: 0.2 }}
                  className="flex-1 min-w-0"
                >
                  <div className="text-sm font-semibold text-text-primary truncate">
                    {family?.name || 'Your Family'}
                  </div>
                  <div className="text-xs text-text-secondary truncate">
                    {user?.displayName || user?.email || 'User'}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
            {!collapsed && (
              <button
                onClick={() => {
                  if (!showUserMenu && userMenuRef.current) {
                    const rect = userMenuRef.current.getBoundingClientRect();
                    setMenuPosition({
                      top: rect.bottom + 8,
                      left: rect.left,
                      width: rect.width
                    });
                  }
                  setShowUserMenu(!showUserMenu);
                }}
                className="text-text-secondary hover:text-text-primary transition-colors"
              >
                <FontAwesomeIcon icon={showUserMenu ? faChevronUp : faChevronDown} className="w-3 h-3" />
              </button>
            )}
          </div>
        </div>

        {/* Main Navigation */}
        <nav className="flex-1 py-4 overflow-y-auto overflow-x-hidden">
          {navItems.map((item, index) => {
            const isActive = activeRoute === item.id;
            const badgeCount = getBadgeCount(item.id);
            const isExperiences = item.id === 'experiences';
            const showExpandArrow = isExperiences && !collapsed && recentExperiences.length > 0;
            
            return (
              <div key={item.id} className={`relative group ${isExperiences ? 'experiences-menu-container' : ''}`}>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => handleNavClick(item)}
                    className={`
                      flex items-center gap-3 rounded-lg flex-1
                      transition-all duration-200
                      ${isActive 
                        ? 'bg-primary text-white' 
                        : 'text-text-primary hover:bg-surface'
                      }
                      ${collapsed ? 'justify-center m-2 p-0 w-auto' : 'w-full px-4 py-3 mx-2'}
                    `}
                    title={collapsed ? `${item.label} (${getKeyboardShortcut(index)})` : ''}
                    style={collapsed ? { 
                      minHeight: '44px',
                      minWidth: '44px',
                      width: '44px',
                      height: '44px'
                    } : { maxWidth: '100%' }}
                  >
                    <FontAwesomeIcon 
                      icon={item.icon} 
                      className={`w-5 h-5 flex-shrink-0 ${collapsed ? 'mx-auto' : ''}`}
                    />
                    <AnimatePresence>
                      {!collapsed && (
                        <motion.span
                          initial={{ opacity: 0, width: 0 }}
                          animate={{ opacity: 1, width: 'auto' }}
                          exit={{ opacity: 0, width: 0 }}
                          transition={{ duration: 0.2 }}
                          className="flex-1 text-left font-medium truncate min-w-0"
                        >
                          {item.label}
                        </motion.span>
                      )}
                    </AnimatePresence>
                    {badgeCount !== null && !showExpandArrow && (
                      <AnimatePresence>
                        {!collapsed ? (
                          <motion.span
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.8 }}
                            className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                              isActive 
                                ? 'bg-white/20 text-white' 
                                : 'bg-primary text-white'
                            }`}
                          >
                            {badgeCount}
                          </motion.span>
                        ) : (
                          <motion.span
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.8 }}
                            className="absolute top-2 right-2 w-2 h-2 rounded-full bg-primary"
                          />
                        )}
                      </AnimatePresence>
                    )}
                  </button>
                  {showExpandArrow && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowExperiencesMenu(!showExperiencesMenu);
                      }}
                      className={`
                        p-1 rounded transition-colors
                        ${isActive 
                          ? 'text-white hover:bg-white/20' 
                          : 'text-text-secondary hover:bg-surface'
                        }
                      `}
                      title="Show recent experiences"
                    >
                      <FontAwesomeIcon 
                        icon={showExperiencesMenu ? faChevronUp : faChevronDown} 
                        className="w-3 h-3 transition-transform"
                      />
                    </button>
                  )}
                </div>
                
                {/* Experiences Quick Switcher Menu */}
                {isExperiences && !collapsed && showExperiencesMenu && recentExperiences.length > 0 && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="ml-4 mr-2 mt-1 mb-2 bg-surface rounded-lg border border-border overflow-hidden overflow-x-hidden"
                  >
                    <div className="px-3 py-2 text-xs font-semibold text-text-secondary uppercase border-b border-border">
                      Recent Experiences
                    </div>
                    {recentExperiences.map((experience) => (
                      <button
                        key={experience.id}
                        onClick={() => handleExperienceClick(experience.id)}
                        className="w-full text-left px-3 py-2 text-sm text-text-primary hover:bg-[#E0E0E0] transition-colors flex items-center justify-between"
                      >
                        <div className="flex-1 min-w-0">
                          <div className="font-medium truncate">{experience.title}</div>
                          <div className="text-xs text-text-secondary">
                            {getExperienceType(experience.type)?.name || experience.type}
                          </div>
                        </div>
                      </button>
                    ))}
                  </motion.div>
                )}
                
                {/* Tooltip for collapsed state */}
                {collapsed && (
                  <div className="absolute left-full ml-2 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-50 whitespace-nowrap">
                    {item.label} ({getKeyboardShortcut(index)})
                    {badgeCount !== null && (
                      <span className="ml-2 px-1.5 py-0.5 bg-primary rounded text-xs">
                        {badgeCount}
                      </span>
                    )}
                  </div>
                )}
              </div>
            );
          })}
          
          {/* Admin Link - only visible to admins */}
          {isAdmin && (
            <div className="relative group">
              <button
                onClick={() => navigate('/admin')}
                className={`
                  flex items-center gap-3 rounded-lg
                  transition-all duration-200
                  ${location.pathname === '/admin'
                    ? 'bg-primary text-white' 
                    : 'text-text-primary hover:bg-surface'
                  }
                  ${collapsed ? 'justify-center m-2 p-0 w-auto' : 'w-full px-4 py-3 mx-2'}
                `}
                title={collapsed ? 'Admin' : ''}
                style={collapsed ? { 
                  minHeight: '44px',
                  minWidth: '44px',
                  width: '44px',
                  height: '44px'
                } : { maxWidth: '100%' }}
              >
                <FontAwesomeIcon 
                  icon={faShield} 
                  className={`w-5 h-5 flex-shrink-0 ${collapsed ? 'mx-auto' : ''}`}
                />
                <AnimatePresence>
                  {!collapsed && (
                    <motion.span
                      initial={{ opacity: 0, width: 0 }}
                      animate={{ opacity: 1, width: 'auto' }}
                      exit={{ opacity: 0, width: 0 }}
                      transition={{ duration: 0.2 }}
                      className="flex-1 text-left font-medium truncate min-w-0"
                    >
                      Admin
                    </motion.span>
                  )}
                </AnimatePresence>
              </button>
              
              {/* Tooltip for collapsed state */}
              {collapsed && (
                <div className="absolute left-full ml-2 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-50 whitespace-nowrap">
                  Admin
                </div>
              )}
            </div>
          )}
        </nav>

        {/* Secondary Section */}
        <div className="border-t border-border py-2">
          {!collapsed && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <button
                onClick={() => {
                  // TODO: Open help modal or navigate to help page
                  toast.info('Help & Support coming soon');
                }}
                className="w-full flex items-center gap-3 px-4 py-3 mx-2 rounded-lg text-text-primary hover:bg-surface transition-colors max-w-full"
              >
                <FontAwesomeIcon icon={faCircleQuestion} className="w-5 h-5" />
                <span className="flex-1 text-left font-medium truncate min-w-0">Help & Support</span>
              </button>
            </motion.div>
          )}
          
          {!collapsed && (
            <motion.button
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={handleSignOut}
              className="w-full flex items-center gap-3 px-4 py-3 mx-2 rounded-lg text-primary hover:bg-primary/10 transition-colors max-w-full"
            >
              <FontAwesomeIcon icon={faRightFromBracket} className="w-5 h-5" />
              <span className="flex-1 text-left font-medium truncate min-w-0">Sign Out</span>
            </motion.button>
          )}

          {/* Toggle Button */}
          <button
            onClick={toggleSidebar}
            className={`flex items-center text-text-secondary hover:text-text-primary transition-colors ${collapsed ? 'justify-end m-2 p-0' : 'w-full justify-center py-3'}`}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            style={collapsed ? { minHeight: '44px' } : {}}
          >
            <FontAwesomeIcon 
              icon={collapsed ? faChevronRight : faChevronLeft} 
              className="w-4 h-4"
            />
          </button>
        </div>
      </aside>

      {/* Search Command Palette */}
      <AnimatePresence>
        {showSearchPalette && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 z-50"
              onClick={() => setShowSearchPalette(false)}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: -20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: -20 }}
              className="fixed top-20 left-1/2 -translate-x-1/2 w-full max-w-lg bg-white rounded-lg shadow-2xl z-50 border border-border"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-4 border-b border-border">
                <div className="flex items-center gap-3">
                  <FontAwesomeIcon icon={faSearch} className="w-5 h-5 text-text-secondary" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search pages, experiences, or navigate..."
                    className="flex-1 text-base border-none outline-none text-text-primary"
                    autoFocus
                  />
                  <span className="text-xs text-text-secondary px-2 py-1 bg-surface rounded">⌘K</span>
                </div>
              </div>
              <div className="max-h-96 overflow-y-auto p-2">
                {/* Quick Navigation */}
                <div className="px-3 py-2 text-xs font-semibold text-text-secondary uppercase">Quick Navigation</div>
                {navItems.map((item, index) => (
                  <button
                    key={item.id}
                    onClick={() => {
                      handleNavClick(item);
                      setShowSearchPalette(false);
                    }}
                    className="w-full text-left px-3 py-2 rounded-lg hover:bg-surface transition-colors flex items-center justify-between"
                  >
                    <div className="flex items-center gap-3">
                      <FontAwesomeIcon icon={item.icon} className="w-4 h-4 text-text-secondary" />
                      <span className="text-sm text-text-primary">{item.label}</span>
                    </div>
                    <span className="text-xs text-text-secondary">{getKeyboardShortcut(index)}</span>
                  </button>
                ))}
                
                {/* Recent Experiences */}
                {recentExperiences.length > 0 && (
                  <>
                    <div className="px-3 py-2 text-xs font-semibold text-text-secondary uppercase mt-2">Recent Experiences</div>
                    {recentExperiences.map((experience) => (
                      <button
                        key={experience.id}
                        onClick={() => {
                          handleExperienceClick(experience.id);
                          setShowSearchPalette(false);
                        }}
                        className="w-full text-left px-3 py-2 rounded-lg hover:bg-surface transition-colors"
                      >
                        <div className="font-medium text-sm text-text-primary">{experience.title}</div>
                        <div className="text-xs text-text-secondary">
                          {getExperienceType(experience.type)?.name || experience.type}
                        </div>
                      </button>
                    ))}
                  </>
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Family Units Switcher Menu - Portal to body to escape overflow */}
      {!collapsed && createPortal(
        <AnimatePresence>
          {showUserMenu && (
            <>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-[9998]"
                onClick={() => setShowUserMenu(false)}
              />
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="fixed bg-white border border-border rounded-lg shadow-lg z-[9999]"
                style={{
                  top: `${menuPosition.top}px`,
                  left: `${menuPosition.left}px`,
                  width: `${menuPosition.width}px`,
                  minWidth: '200px'
                }}
              >
                <div className="p-2">
                  <div className="px-3 py-2 text-xs font-semibold text-text-secondary uppercase">Family Units</div>
                  <div className="px-3 py-2 text-sm text-text-primary">
                    {family?.name || 'Your Family'}
                  </div>
                  <div className="border-t border-border mt-2 pt-2">
                    <div className="px-3 py-2 text-xs text-text-secondary">
                      Multi-family support coming soon
                    </div>
                  </div>
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>,
        document.body
      )}
    </>
  );
}

